import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Trash2, ShieldCheck, Loader2, ChevronLeft, Mail, Phone, Car, MapPin, CalendarDays, List, Truck,
} from "lucide-react";
import { AdminCalendar } from "./AdminCalendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const t = localStorage.getItem("cc_session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS = {
  nuovo: { label: "Nuovo", cls: "bg-[#7EC8E3]/15 text-[#A0E0FF] border-[#7EC8E3]/40" },
  confermato: { label: "Confermato", cls: "bg-green-400/15 text-green-200 border-green-400/40" },
  completato: { label: "Completato", cls: "bg-slate-400/15 text-slate-200 border-slate-400/40" },
  annullato: { label: "Annullato", cls: "bg-red-400/15 text-red-200 border-red-400/40" },
};

export const AdminDashboard = () => {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [view, setView] = useState("calendar");
  const [blockedDays, setBlockedDays] = useState([]);
  const [quotes, setQuotes] = useState([]);

  const loadQuotes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/fleet-quotes`, { headers: authHeaders(), credentials: "include" });
      if (res.ok) setQuotes(await res.json());
    } catch (e) { /* ignore */ }
  }, []);

  const loadBlockedDays = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/blocked-days`, { headers: authHeaders(), credentials: "include" });
      if (res.ok) setBlockedDays(await res.json());
    } catch (e) { /* ignore */ }
  }, []);

  const toggleDay = async (date, isBlocked) => {
    try {
      if (isBlocked) {
        if (!window.confirm(`Sbloccare la giornata ${date}?`)) return;
        const res = await fetch(`${API}/admin/blocked-days/${date}`, { method: "DELETE", headers: authHeaders(), credentials: "include" });
        if (!res.ok) throw new Error();
        setBlockedDays((b) => b.filter((x) => x.date !== date));
        toast.success("Giornata sbloccata");
      } else {
        const reason = window.prompt(`Bloccare la giornata ${date}? Motivo (opzionale, es. Ferie):`, "Ferie");
        if (reason === null) return;
        const res = await fetch(`${API}/admin/blocked-days`, {
          method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
          credentials: "include", body: JSON.stringify({ date, reason }),
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setBlockedDays((b) => [...b.filter((x) => x.date !== date), { date, reason: data.reason }]);
        toast.success("Giornata bloccata");
      }
    } catch (e) {
      toast.error("Operazione non riuscita");
    }
  };

  const loadBookings = useCallback(async () => {
    setLoadingBookings(true);
    try {
      const res = await fetch(`${API}/admin/bookings`, {
        headers: authHeaders(),
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      setBookings(await res.json());
    } catch (e) {
      toast.error("Impossibile caricare le prenotazioni");
    } finally {
      setLoadingBookings(false);
    }
  }, []);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${API}/auth/me`, {
          headers: authHeaders(),
          credentials: "include",
        });
        if (!res.ok) throw new Error();
        const u = await res.json();
        setUser(u);
      } catch (e) {
        setUser(false);
      } finally {
        setLoading(false);
      }
    };
    if (!user) check();
  }, [user]);

  useEffect(() => {
    if (user && user.is_admin) { loadBookings(); loadBlockedDays(); loadQuotes(); }
  }, [user, loadBookings, loadBlockedDays, loadQuotes]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", headers: authHeaders(), credentials: "include" });
    } catch (e) { /* ignore */ }
    localStorage.removeItem("cc_session_token");
    setUser(false);
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/admin/bookings/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setBookings((b) => b.map((x) => (x.id === id ? { ...x, status } : x)));
      if (data.email_sent === true) toast.success("Stato aggiornato · email di conferma inviata al cliente");
      else if (data.email_sent === false) toast.warning("Stato aggiornato, ma invio email al cliente fallito");
      else toast.success("Stato aggiornato");
    } catch (e) {
      toast.error("Errore aggiornamento stato");
    }
  };

  const remove = async (id) => {
    try {
      const res = await fetch(`${API}/admin/bookings/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      setBookings((b) => b.filter((x) => x.id !== id));
      toast.success("Prenotazione eliminata");
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050B18] text-[#A0E0FF]">
        <Loader2 className="animate-spin mr-2" /> Caricamento…
      </div>
    );
  }

  // Not authenticated OR not admin -> login / unauthorized screen
  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050B18] px-4">
        <div className="card-metallic rounded-3xl p-10 max-w-md w-full text-center">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0D1B4A] to-[#0A1A3A] border border-[#7EC8E3]/40 flex items-center justify-center">
            <ShieldCheck size={30} className="text-[#A0E0FF]" />
          </div>
          <h1 className="text-2xl font-bold mt-5 text-slate-100 font-display">Area riservata</h1>
          {user && !user.is_admin ? (
            <>
              <p className="text-slate-300 mt-3">
                L'account <b>{user.email}</b> non è autorizzato ad accedere a quest'area.
              </p>
              <button data-testid="admin-logout-button" onClick={logout} className="btn-outline-blue rounded-full px-6 py-3 mt-6 font-semibold">
                Esci
              </button>
            </>
          ) : (
            <>
              <p className="text-slate-300 mt-3">
                Accedi con il tuo account Google autorizzato per gestire le prenotazioni.
              </p>
              <button data-testid="admin-login-button" onClick={login} className="btn-silver rounded-full px-7 py-3.5 mt-6 w-full">
                Accedi con Google
              </button>
            </>
          )}
          <a href="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-[#A0E0FF] mt-6">
            <ChevronLeft size={15} /> Torna al sito
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050B18]" data-testid="admin-dashboard">
      <header className="glass sticky top-0 z-40 px-4 sm:px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Cosimiroclean" className="h-9 w-auto" />
          <div>
            <div className="font-display font-bold text-slate-100 leading-none">Area Admin</div>
            <div className="text-[11px] text-[#7EC8E3]">{user.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href="/" className="text-sm text-slate-300 hover:text-[#A0E0FF] px-3 py-2">Sito</a>
          <button data-testid="admin-logout-button" onClick={logout} className="btn-outline-blue rounded-full px-4 py-2 text-sm inline-flex items-center gap-2">
            <LogOut size={15} /> Esci
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-100 font-display">
            Prenotazioni <span className="text-[#7EC8E3]">({bookings.length})</span>
          </h2>
          <div className="flex rounded-full border border-slate-500/25 bg-[#050B18]/60 p-1 text-sm">
            <button data-testid="admin-view-calendar" onClick={() => setView("calendar")}
              className={`rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors ${view === "calendar" ? "bg-[#7EC8E3]/20 text-[#A0E0FF]" : "text-slate-400 hover:text-slate-200"}`}>
              <CalendarDays size={15} /> Calendario
            </button>
            <button data-testid="admin-view-list" onClick={() => setView("list")}
              className={`rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors ${view === "list" ? "bg-[#7EC8E3]/20 text-[#A0E0FF]" : "text-slate-400 hover:text-slate-200"}`}>
              <List size={15} /> Elenco
            </button>
            <button data-testid="admin-view-quotes" onClick={() => setView("quotes")}
              className={`rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors ${view === "quotes" ? "bg-[#7EC8E3]/20 text-[#A0E0FF]" : "text-slate-400 hover:text-slate-200"}`}>
              <Truck size={15} /> Aziende ({quotes.length})
            </button>
          </div>
        </div>

        {view === "calendar" && !loadingBookings && <div className="mb-8"><AdminCalendar bookings={bookings} blockedDays={blockedDays} onToggleDay={toggleDay} /></div>}

        {view === "quotes" && (
          <div className="space-y-4" data-testid="admin-quotes-list">
            {quotes.length === 0 && <div className="card-metallic rounded-2xl p-10 text-center text-slate-400">Nessuna richiesta preventivo aziende.</div>}
            {quotes.map((q) => (
              <div key={q.id} data-testid={`admin-quote-${q.id}`} className="card-metallic rounded-2xl p-5">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-bold text-slate-100 text-lg">{q.company}</span>
                  {q.vehicles_count > 5 && <span className="text-xs px-2.5 py-1 rounded-full border bg-green-400/15 text-green-200 border-green-400/40">Flotta &gt;5</span>}
                  <span className="text-xs text-slate-500">{new Date(q.created_at).toLocaleString("it-IT")}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-3 text-sm text-slate-300">
                  <span>Referente: <b className="text-slate-100">{q.contact_name}</b></span>
                  <span className="flex items-center gap-2"><Phone size={14} className="text-[#7EC8E3]" /> {q.phone}</span>
                  <span className="flex items-center gap-2"><Mail size={14} className="text-[#7EC8E3]" /> {q.email}</span>
                  <span className="flex items-center gap-2"><MapPin size={14} className="text-[#7EC8E3]" /> {q.location}</span>
                  <span className="flex items-center gap-2"><Truck size={14} className="text-[#7EC8E3]" /> {q.vehicles_count} mezzi · {q.vehicle_types?.join(", ")}</span>
                </div>
                {q.notes ? <p className="mt-2 text-sm text-slate-400 italic">“{q.notes}”</p> : null}
              </div>
            ))}
          </div>
        )}

        {loadingBookings ? (
          <div className="text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Caricamento…</div>
        ) : view === "list" && bookings.length === 0 ? (
          <div className="card-metallic rounded-2xl p-10 text-center text-slate-400">
            Nessuna prenotazione ricevuta al momento.
          </div>
        ) : view === "list" ? (
          <div className="space-y-4" data-testid="admin-bookings-list">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`admin-booking-${b.id}`} className="card-metallic rounded-2xl p-5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-bold text-slate-100 text-lg">
                        {b.first_name} {b.last_name}
                      </span>
                      <span className={`text-xs px-2.5 py-1 rounded-full border ${(STATUS[b.status] || STATUS.nuovo).cls}`}>
                        {(STATUS[b.status] || STATUS.nuovo).label}
                      </span>
                      <span className="text-xs text-slate-500">
                        {new Date(b.created_at).toLocaleString("it-IT")}
                      </span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-3 text-sm text-slate-300">
                      <span className="flex items-center gap-2"><Phone size={14} className="text-[#7EC8E3]" /> {b.phone}</span>
                      <span className="flex items-center gap-2"><Mail size={14} className="text-[#7EC8E3]" /> {b.email}</span>
                      <span className="flex items-center gap-2"><Car size={14} className="text-[#7EC8E3]" /> {b.plate || "—"}</span>
                      <span className="flex items-center gap-2"><MapPin size={14} className="text-[#7EC8E3]" /> {b.location || "—"}{b.distance_km != null ? ` (${b.distance_km} km)` : ""}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      {b.breakdown?.map((it, i) => (
                        <span key={i} className="rounded-full bg-[#050B18]/60 border border-slate-500/20 px-3 py-1 text-slate-300">
                          {it.label} · {it.price}€
                        </span>
                      ))}
                    </div>
                    <div className="mt-3 text-sm">
                      <span className="text-slate-400">Appuntamento: </span>
                      <span className="text-slate-200 font-medium">{b.date ? `${b.date} · ${b.time}` : "Data da concordare"}</span>
                      <span className="text-slate-400 ml-4">Totale: </span>
                      <span className="text-[#A0E0FF] font-bold">{b.total}€</span>
                    </div>
                    {b.notes ? <p className="mt-2 text-sm text-slate-400 italic">“{b.notes}”</p> : null}
                  </div>

                  <div className="flex flex-row lg:flex-col gap-2 shrink-0">
                    <select
                      data-testid={`admin-status-select-${b.id}`}
                      value={b.status || "nuovo"}
                      onChange={(e) => updateStatus(b.id, e.target.value)}
                      className="rounded-xl bg-[#050B18]/70 border border-slate-500/25 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-[#7EC8E3]"
                    >
                      <option value="nuovo">Nuovo</option>
                      <option value="confermato">Confermato</option>
                      <option value="completato">Completato</option>
                      <option value="annullato">Annullato</option>
                    </select>
                    <button
                      data-testid={`admin-delete-${b.id}`}
                      onClick={() => remove(b.id)}
                      className="rounded-xl border border-red-400/40 text-red-200 hover:bg-red-400/10 px-3 py-2 text-sm inline-flex items-center justify-center gap-1.5 transition-colors"
                    >
                      <Trash2 size={15} /> Elimina
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </main>
    </div>
  );
};
